/**
 * OIDC (Keycloak) configuration for the SPA.
 *
 * oidc-client-ts / react-oidc-context replace MSAL as of plan §6. This file
 * exports the `UserManagerSettings` shape oidc-client-ts expects, consumed
 * directly by react-oidc-context's <AuthProvider> in ../components/AuthProvider.js.
 */

import { WebStorageStateStore } from "oidc-client-ts";
import {
  getOidcClientId,
  getOidcAuthority,
} from "../services/runtimeConfig";

// Three layers, in priority order: the deployed container's injected
// window.__APP_CONFIG__ (rendered by frontend/docker-entrypoint.sh from the
// OIDC_CLIENT_ID/OIDC_AUTHORITY env vars — §6.7), then compile-time
// REACT_APP_OIDC_* env (set only by a local .env, not by any tracked
// compose file), then the hardcoded fallback below, which matches this
// repo's local Keycloak dev realm (docker-compose.local.yml's `keycloak`
// service, realm "platform"). No tenant id is read at any layer: Keycloak's
// realm is baked into the authority URL rather than passed separately, so
// there is no Keycloak counterpart to Entra's tenant id.
const CLIENT_ID =
  getOidcClientId() || process.env.REACT_APP_OIDC_CLIENT_ID || "platform-spa";
const AUTHORITY =
  getOidcAuthority() ||
  process.env.REACT_APP_OIDC_AUTHORITY ||
  "http://localhost:8081/realms/platform";
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || window.location.origin;

/**
 * oidc-client-ts UserManagerSettings, consumed by react-oidc-context's
 * <AuthProvider>.
 *
 * Session/storage policy is PARITY with what MSAL did (§6.8) — a storage
 * change is a separate decision with its own security argument, not this
 * task's:
 *  - sessionStorage for BOTH the user store and the interaction-state
 *    store. oidc-client-ts defaults both to window.localStorage, so both
 *    must be set explicitly or tokens (and in-flight signin state) outlive
 *    the tab, unlike MSAL's `cacheLocation: "sessionStorage"`.
 *  - Authorization code flow with PKCE (oidc-client-ts's default;
 *    `disablePKCE` is never set) — the same flow MSAL used.
 *  - automaticSilentRenew: refresh-token-based renewal, no hidden iframe,
 *    as long as Keycloak issues a refresh token (it does, for this client).
 */
export const oidcConfig = {
  authority: AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: REDIRECT_URI,
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  // "organization" is NOT optional garnish, though a stock Keycloak realm
  // assigns the `organization` client scope as optional. §4.3 moved it into
  // `platform-spa`'s `defaultClientScopes` in the committed realm
  // (infra/keycloak/realm-platform.json, pinned by
  // test_keycloak_realm_platform.py's
  // test_organization_is_a_default_scope_on_platform_spa), so the claim is
  // emitted on every login regardless of this scope string. Requesting it
  // explicitly here is the second line of defence: if that default were
  // ever demoted back to optional — a demotion
  // test_keycloak_config_drift.py's
  // test_organization_demoted_from_default_to_optional_is_reported exists
  // to catch — this string still asks for the claim. Lose both and every
  // login resolves no tenant, per §2.3: a refused login for every user at
  // once, not a degraded feature.
  scope: "openid profile email organization",
  loadUserInfo: false,
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
};

/**
 * Build the `signinRedirect` args that carry the current URL through the
 * login round-trip via OIDC `state` — round-tripped onto the resolved
 * `User` (`user.state`), NOT stashed in sessionStorage: `state` is bound to
 * the authorization request itself and survives a page reload mid-flow.
 *
 * This is the oidc-client-ts equivalent of MSAL's
 * `navigateToLoginRequestUrl: true`, which had no built-in counterpart
 * here (verified against the installed oidc-client-ts/react-oidc-context
 * source: `AuthProvider` resolves the callback via
 * `userManager.signinCallback()` and hands the result to `onSigninCallback`,
 * whose `user.state` is exactly whatever was passed to `signinRedirect`).
 * `/cli-auth`'s single-use challenge query parameters depend on this: a
 * login started from `/cli-auth?port=...&challenge=...` must return the
 * browser there, or `tct auth login` hangs at the loopback listener with no
 * error.
 *
 * @returns {{state: {returnTo: string}}}
 */
export function buildSigninRedirectArgs() {
  return {
    state: { returnTo: `${window.location.pathname}${window.location.search}` },
  };
}
